import { z } from 'zod';
import { creerEntierOuNull, depuisJson, entierOuNull } from './champs';

/**
 * Projet de rattachement, facultatif : le formulaire propose un « accès libre »
 * qui laisse project_id à null. Quand un projet est demandé, l'identifiant doit
 * être positif, ce qui écarte le '-1' que FormulaireSelectionProjet utilise
 * pour « aucun projet ».
 */
const projetFacultatif = creerEntierOuNull({
  message: 'selectionner_un_projet',
  min: 1,
});

/**
 * Le gabarit lui-même : une liste de points, saisie en JSON dans une zone de
 * texte, par exemple [[12,23],[45,56]].
 *
 * Jusqu'ici, un JSON illisible faisait échouer JSON.parse dans reqEnvoi : rien
 * ne partait, et seule la console le signalait. L'utilisateur cliquait sans
 * résultat ni message.
 *
 * Seule la forme de liste est exigée, pas celle de chaque point : les données
 * existantes n'ont pas pu être examinées, et une contrainte plus fine risquerait
 * d'empêcher la modification de gabarits déjà enregistrés.
 */
const messageGabarit = { message: 'format_gabarit_attendu' };
const gabarit = z.preprocess(depuisJson, z.array(z.any(), messageGabarit));

const champs = {
  template_name: z
    .string({ message: 'saisir_un_nom_de_gabarit' })
    .trim()
    .min(1, { message: 'saisir_un_nom_de_gabarit' }),

  project_id: projetFacultatif,

  // Début et fin d'application, en secondes UNIX, chacun facultatif.
  start: entierOuNull,
  end: entierOuNull,

  private: entierOuNull,

  // Pas de liste fermée : un niveau renvoyé par l'API et absent du glossaire
  // bloquerait sinon la modification sans message visible.
  alarm_level: entierOuNull,

  template: gabarit,
};

// Quand les deux bornes sont renseignées, une fin antérieure au début ne
// désigne aucune période. Le message se pose sous la date de fin.
const intervalleOrdonne = (schema) =>
  schema.refine(
    ({ start, end }) => start === null || end === null || start < end,
    { message: 'date_de_fin_avant_date_de_debut', path: ['end'] }
  );

/**
 * Schéma de la charge utile envoyée à la création d'un gabarit d'alarme.
 *
 * FormulaireGabarit sert à la création comme à la modification. z.object
 * retirant les clés qu'il ne déclare pas, les 7 clés de la charge utile sont
 * toutes déclarées. Le nom et un gabarit en liste sont exigés ; le projet
 * reste facultatif, l'accès libre étant un choix proposé par le formulaire.
 *
 * Le schéma est construit deux fois plutôt qu'étendu : Zod refuse d'étendre un
 * objet qui porte déjà une règle, ici celle de l'intervalle.
 */
export const schemaNouveauGabaritAlarme = intervalleOrdonne(z.object(champs));

/**
 * Schéma de la charge utile envoyée à la modification d'un gabarit d'alarme.
 * Même contrat, plus l'identifiant du gabarit visé.
 */
export const schemaModificationGabaritAlarme = intervalleOrdonne(
  z.object({
    ...champs,
    template_id: z.coerce.number({ message: 'valeur_invalide' }).int(),
  })
);
